import { CSSProperties } from 'react';
import { themeObject } from '../../../lib';

export interface MetadataResultAccordion {
    name: string;
    accordionAriaControls: string;
    accordionSumID: string;
    accordionHeader: MetadataSectionName;
    expanded?: boolean;
    onChange?: (_: any, isExpanded: any) => void;
    key: MetadataSectionKey;
    data: any[];
}

export interface MetadataSearchResultsProps {
    initialTerms: string[];
}

export interface DefaultViewProps {
    onTermsChanged: any;
    onSubmit: () => void;
    fieldValue: string[];
}

export type MetadataResultAccordionProps = Omit<
    MetadataResultAccordion,
    'key' | 'name'
>;

export interface DrawerItem {
    type: DrawerItemType;
    title: string;
    value: any; // value can be dictionary, user group, glossary, etc.
}

export type MetadataSectionKey =
    | 'dictionaries'
    | 'edgeDefs'
    | 'userGroups'
    | 'userWallets'
    | 'systemWallets'
    | 'glossaries'
    | 'terms'
    | 'dataPipelines';
export type MetadataSectionName =
    | ''
    | 'Dictionaries'
    | 'Edge Definitions'
    | 'User Groups'
    | 'User Wallets'
    | 'System Wallets'
    | 'Glossary'
    | 'Terms'
    | 'Data Pipelines';
export type DrawerItemType =
    | 'Dictionary'
    | 'Edge Definition'
    | 'System Wallet'
    | 'User Group'
    | 'User Wallet'
    | 'Glossary'
    | 'Data Pipeline'
    | 'Term'
    | 'N/A';

export type MetadataSections = {
    [key in MetadataSectionKey]: MetadataSectionName;
};

export const metadataSections: MetadataSections = {
    dictionaries: 'Dictionaries',
    edgeDefs: 'Edge Definitions',
    userGroups: 'User Groups',
    userWallets: 'User Wallets',
    systemWallets: 'System Wallets',
    glossaries: 'Glossary',
    terms: 'Terms',
    dataPipelines: 'Data Pipelines',
};

export type DrawerItemWithoutValue = Omit<DrawerItem, 'value'>;

export type MetadataDrawerItem = {
    [key in MetadataSectionKey]: DrawerItemType;
};

export const metadataDrawerItemType: MetadataDrawerItem = {
    dictionaries: 'Dictionary',
    edgeDefs: 'Edge Definition',
    userGroups: 'User Group',
    userWallets: 'User Wallet',
    systemWallets: 'System Wallet',
    glossaries: 'Glossary',
    terms: 'Term',
    dataPipelines: 'Data Pipeline',
};

type EmptyMetadataSearchResult = {
    [key in MetadataSectionKey]: [];
};

export const emptyDataResults: EmptyMetadataSearchResult = {
    dictionaries: [],
    edgeDefs: [],
    userGroups: [],
    userWallets: [],
    systemWallets: [],
    glossaries: [],
    terms: [],
    dataPipelines: [],
};

export const metadataResultStyles = {
    resultTextContainer: {
        display: 'flex',
        py: 1.5,
    },
    resultText: {
        ...themeObject.typography.body1,
        color: themeObject.palette.primary[700],
    },
    boldedResultText: {
        ...themeObject.typography.body1,
        color: themeObject.palette.primary[700],
        fontWeight: 700,
    },
    customSearchFieldStyles: {
        minWidth: '407px',
        maxWidth: '100%',
    },
    resultsPageContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        px: 4,
        height: '100%',
    },
    tableComponent: {
        mx: 2.25,
        mb: 1.625,
        '& .MuiDataGrid-main': {
            '& .MuiDataGrid-virtualScroller': {
                '& .MuiDataGrid-virtualScrollerContent': {
                    '& .MuiDataGrid-virtualScrollerRenderZone': {
                        '& .MuiDataGrid-row': {
                            '& .MuiDataGrid-cell': {
                                overflow: 'hidden !important',
                            },
                        },
                    },
                },
            },
        },
    },
    accordionEmptyStateContainer: {
        mx: 44.24,
        mb: 4.875,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    emptyStateImage: {
        width: '145.92px',
        height: '80px',
    },
    emptyStateHeadingContainer: {
        mt: 1.5,
    },
    emptyStateHeadingTextContainer: {
        ...themeObject.typography.h5,
        color: themeObject.palette.primary[700],
    },
};

export const ACCORDION_GRID_HEIGHT = 550;
export const ACCORDION_GRID_MAX_GRID_SIZE = `calc(100vh - ${ACCORDION_GRID_HEIGHT}px)`;
export const DEFAULT_PAGINATION_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGINATION_SIZE = 10;
export const METADATA_SEARCH_TERMS_LIMIT = 20;

export type Column = {
    field: string;
    headerName: string;
    editable: boolean;
    flex: number;
    width?: number;
    headerClassName: string;
};

// move these columnHeaders when the real landing pages/components are created

const getColumnByFieldAndHeaderNames = (
    field: string,
    header: string
): Column => {
    return {
        field: field,
        headerName: header,
        width: 150,
        editable: false,
        flex: 1,
        headerClassName: 'dictionary-grid-header',
    };
};
export const getGlossaryColumns = (): Column[] => {
    return [
        getColumnByFieldAndHeaderNames('term', 'Term'),
        getColumnByFieldAndHeaderNames('type', 'Type'),
        getColumnByFieldAndHeaderNames('description', 'Description'),
        getColumnByFieldAndHeaderNames('groupName', 'Group Name'),
    ];
};

export const getDataPipelineColumns = (): Column[] => {
    return [
        getColumnByFieldAndHeaderNames('name', 'Name'),
        getColumnByFieldAndHeaderNames('description', 'Description'),
    ];
};

export const getTermsColumns = (): Column[] => {
    return [
        getColumnByFieldAndHeaderNames('name', 'Name'),
        getColumnByFieldAndHeaderNames('version', 'Version'),
        getColumnByFieldAndHeaderNames('type', 'Type'),
        getColumnByFieldAndHeaderNames('active', 'Active'),
    ];
};

export const metadataDrawerStyles = {
    jsonContainer: {
        width: '570px',
        minHeight: '300px',
        maxHeight: '750px',
    },
    subheaderInnerContainer: {
        display: 'inline-flex',
        alignItems: 'flex-end',
        pt: 1,
    },
    buttonContainer: {
        pt: 1,
    },
};

export const metadataEditButtonStyle: CSSProperties = {
    position: 'relative',
    margin: 0,
    width: '56px',
    height: '30px',
};

export const truncateDrawerItemTitleWithEllipses = (value: string): string => {
    return value.length > 50 ? value.slice(0, 50) + '...' : value;
};
